/**
 * 路由构建器
 * 将后端菜单树转换为 Vue Router 路由配置
 *
 * @author system
 * @version 1.0.0
 */

import { componentLoader, getIcon } from './componentLoader'

/**
 * 路由构建器类
 * 负责将后端返回的菜单树数据转换为 Vue Router 可用的路由配置
 */
export class RouteBuilder {
  /**
   * 构建路由配置
   * 将菜单树转换为 Vue Router 路由配置数组
   *
   * @param {Array} menuTree - 后端返回的菜单树数据
   * @returns {Array} Vue Router 路由配置数组
   *
   * @example
   * const builder = new RouteBuilder()
   * const routes = builder.buildRoutes(menuTree)
   * routes.forEach(route => router.addRoute(route))
   */
  buildRoutes(menuTree) {
    if (!menuTree || !Array.isArray(menuTree) || menuTree.length === 0) {
      return []
    }

    // 先处理菜单树，将 'C' 类型菜单下的 'C' 类型子菜单提升到父级目录
    const processedTree = this.promoteMenuTypeChildren(menuTree)

    const routes = processedTree
      .filter(menu => menu.menuType !== 'F') // 过滤按钮权限
      .map(menu => this.transformMenuItem(menu))
      .filter(route => route !== null)
      // 防御：确保所有路由 path 以 / 开头，避免 Vue Router 报错
      .map(route => {
        if (route.path && !route.path.startsWith('/')) {
          console.warn('[RouteBuilder] 修复无效路由 path:', route.path, '->', '/' + route.path)
          return { ...route, path: '/' + route.path }
        }
        return route
      })

    return routes
  }

  /**
   * 将 'C' 类型菜单下的 'C' 类型子菜单提升到父级目录
   * 这样可以避免嵌套路由问题
   *
   * @param {Array} menuTree - 原始菜单树
   * @returns {Array} 处理后的菜单树
   *
   * @private
   */
  promoteMenuTypeChildren(menuTree) {
    const result = []

    for (const menu of menuTree) {
      // 复制菜单项
      const processedMenu = { ...menu }

      if (menu.menuType === 'M' && menu.children && menu.children.length > 0) {
        // 目录类型：递归处理子菜单，并收集需要提升的子菜单
        const processedChildren = []
        const promotedChildren = []

        for (const child of menu.children) {
          if (child.menuType === 'C' && child.children && child.children.length > 0) {
            // 'C' 类型菜单有子菜单，需要提升
            const childCopy = { ...child }
            const grandchildren = child.children.filter(gc => gc.menuType === 'C')

            // 将 'C' 类型的孙菜单提升为子菜单
            for (const grandchild of grandchildren) {
              const promoted = {
                ...grandchild,
                // 提升后的路径 = 父菜单路径 + 原路径
                path: `${child.path}/${grandchild.path}`,
                // 保持在同一级目录下
                _promotedFrom: child.id
              }
              promotedChildren.push(promoted)
            }

            // 只保留非 'C' 类型的子菜单（按钮权限等）
            childCopy.children = child.children.filter(gc => gc.menuType !== 'C')
            if (childCopy.children.length === 0) {
              delete childCopy.children
            }
            processedChildren.push(childCopy)
          } else {
            processedChildren.push(child)
          }
        }

        // 合并提升的子菜单
        processedMenu.children = [...processedChildren, ...promotedChildren]
      } else if (menu.menuType === 'C' && menu.children && menu.children.length > 0) {
        // 'C' 类型菜单：将 'C' 类型的子菜单提升到根级别
        const grandchildren = menu.children.filter(gc => gc.menuType === 'C')
        const otherChildren = menu.children.filter(gc => gc.menuType !== 'C')

        // 只保留非 'C' 类型的子菜单
        processedMenu.children = otherChildren.length > 0 ? otherChildren : undefined

        // 提升的子菜单到根级别（路径已拼接好：menu.path/grandchild.path）
        for (const grandchild of grandchildren) {
          result.push({
            ...grandchild,
            path: `${menu.path}/${grandchild.path}`,
            _promotedFrom: menu.id,
            parentId: menu.parentId
          })
        }

        // 如果有 C 型子菜单被提升了，本 C 型菜单不作为根级路由加入
        // （它的子菜单已作为根路由存在，再加会冲突）
        if (grandchildren.length > 0) {
          return result
        }
      }

      result.push(processedMenu)
    }

    return result
  }

  /**
   * 转换单个菜单项为路由配置
   *
   * @param {Object} menu - 菜单项对象
   * @param {string} parentPath - 父级路径（用于构建完整路径）
   * @returns {Object|null} 路由配置对象，如果是按钮类型则返回 null
   *
   * @private
   */
  transformMenuItem(menu, parentPath = '') {
    // 按钮类型（F）- 不生成路由
    if (menu.menuType === 'F') {
      return null
    }

    // 目录类型（M）- 一级菜单容器
    if (menu.menuType === 'M') {
      return this.buildDirectoryRoute(menu, parentPath)
    }

    // 菜单类型（C）- 具体页面
    if (menu.menuType === 'C') {
      return this.buildMenuRoute(menu, parentPath)
    }

    // 未知类型
    console.warn('[RouteBuilder] 未知的菜单类型:', menu.menuType, menu)
    return null
  }

  /**
   * 构建目录类型路由（一级菜单或二级目录）
   *
   * @param {Object} menu - 目录菜单项
   * @param {string} parentPath - 父级路径（用于二级目录嵌套）
   * @returns {Object} 路由配置
   *
   * @private
   */
  buildDirectoryRoute(menu, parentPath = '') {
    // 如果有父级路径，二级目录作为嵌套路由处理
    const isNested = !!parentPath
    const routePath = isNested ? menu.path : this.normalizePath(menu.path, true)
    // 计算完整路径，用于菜单导航
    let fullPath
    if (!isNested) {
      fullPath = routePath
    } else if (menu.path && menu.path.startsWith('/')) {
      // 嵌套目录的path已经是绝对路径，直接使用
      fullPath = menu.path
    } else {
      // 相对路径，拼接父路径
      fullPath = `${parentPath}/${menu.path}`
    }

    const route = {
      path: routePath,
      name: this.generateRouteName(menu),
      // 一级目录用 Layout，二级目录用 Empty（router-view 占位）
      component: componentLoader(isNested ? 'Empty' : 'Layout'),
      meta: {
        title: menu.menuName,
        icon: getIcon(menu.icon),
        hidden: menu.visible !== 1, // visible=1表示显示，所以hidden应为false
        perms: this.parsePerms(menu.perms),
        menuType: 'M',
        menuId: menu.id,
        parentId: menu.parentId,
        orderNum: menu.orderNum,
        fullPath: fullPath  // 添加完整路径
      }
    }

    // 处理子菜单
    if (menu.children && menu.children.length > 0) {
      // 子路由使用相对路径，但传递 fullPath 用于计算子路由的 fullPath
      const childRoutes = menu.children
        .filter(child => child.menuType !== 'F')
        .map(child => this.transformMenuItem(child, fullPath))  // 传递完整路径
        .filter(child => child !== null)

      if (childRoutes.length > 0) {
        route.children = childRoutes
        // 设置默认重定向到第一个子路由（使用相对路径）
        route.redirect = childRoutes[0].path
      }
    }

    return route
  }

  /**
   * 构建菜单类型路由（具体页面）
   *
   * @param {Object} menu - 菜单项
   * @param {string} parentPath - 父级路径
   * @returns {Object} 路由配置
   *
   * @private
   */
  buildMenuRoute(menu, parentPath) {
    const routePath = menu.path // 子路由使用相对路径
    const fullPath = parentPath ? `${parentPath}/${routePath}` : `/${routePath}`

    const route = {
      path: routePath,
      name: this.generateRouteName(menu),
      component: componentLoader(menu.component),
      meta: {
        title: menu.menuName,
        icon: getIcon(menu.icon),
        hidden: menu.visible !== 1, // visible=1表示显示，所以hidden应为false
        perms: this.parsePerms(menu.perms),
        menuType: 'C',
        menuId: menu.id,
        parentId: menu.parentId,
        orderNum: menu.orderNum,
        fullPath: fullPath,
        keepAlive: true // 默认开启页面缓存
      }
    }

    return route
  }

  /**
   * 生成唯一的路由名称
   *
   * @param {Object} menu - 菜单项
   * @returns {string} 路由名称
   *
   * @private
   */
  generateRouteName(menu) {
    // 使用路径和ID组合生成唯一名称
    const pathPart = menu.path
      .replace(/^\//, '') // 移除开头斜杠
      .replace(/\//g, '_') // 替换中间斜杠为下划线
      .replace(/[^a-zA-Z0-9_]/g, '') // 移除非法字符

    // 首字母大写
    const capitalizedPath = pathPart.charAt(0).toUpperCase() + pathPart.slice(1)

    return `${capitalizedPath}_${menu.id}`
  }

  /**
   * 规范化路径
   *
   * @param {string} path - 原始路径
   * @param {boolean} isRoot - 是否为根路径
   * @returns {string} 规范化后的路径
   *
   * @private
   */
  normalizePath(path, isRoot = false) {
    if (!path) return '/'

    // 移除开头和结尾的斜杠
    let normalizedPath = path.replace(/^\/+|\/+$/g, '')

    // 根路径添加开头斜杠
    if (isRoot) {
      normalizedPath = '/' + normalizedPath
    }

    return normalizedPath
  }

  /**
   * 解析权限字符串
   *
   * @param {string} perms - 权限字符串（逗号分隔）
   * @returns {Array} 权限数组
   *
   * @private
   */
  parsePerms(perms) {
    if (!perms) return []
    return perms.split(',').map(p => p.trim()).filter(p => p)
  }
}

/**
 * 创建路由构建器实例的工厂函数
 *
 * @returns {RouteBuilder} 路由构建器实例
 */
export function createRouteBuilder() {
  return new RouteBuilder()
}

export default RouteBuilder
